import { useEffect, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { api } from "../lib/api";

const blankEntry = () => ({
  key: `k-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  type: "expense", category: "", subcategory: "", description: "",
  amount: 0, day_of_month: 1, comments: "",
});

const blankTemplate = { name: "", description: "", initial_balance: 0, entries: [] };

export const TemplateEditor = ({ open, onOpenChange, template, onSaved }) => {
  const [form, setForm] = useState(blankTemplate);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.listCategories().then(setCategories);
      const src = template || blankTemplate;
      setForm({ ...src, entries: (src.entries || []).map((e) => ({ ...e, key: e.id || blankEntry().key })) });
    }
  }, [open, template]);

  const updateEntry = (key, patch) =>
    setForm((f) => ({ ...f, entries: f.entries.map((e) => (e.key === key ? { ...e, ...patch } : e)) }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Ingresa un nombre para la plantilla"); return; }
    const invalid = form.entries.find((e) => !e.category || !e.description.trim());
    if (invalid) { toast.error("Cada partida necesita categoría y descripción"); return; }
    const payload = {
      name: form.name,
      description: form.description,
      initial_balance: parseFloat(form.initial_balance) || 0,
      entries: form.entries.map(({ key, ...e }) => ({
        ...e,
        amount: parseFloat(e.amount) || 0,
        day_of_month: Math.min(Math.max(parseInt(e.day_of_month) || 1, 1), 31),
      })),
    };
    setSaving(true);
    try {
      if (template?.id) await api.updateTemplate(template.id, payload);
      else await api.createTemplate(payload);
      toast.success(template?.id ? "Plantilla actualizada" : "Plantilla creada");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar plantilla");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="template-editor-dialog" className="rounded-sm max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight">
            {template?.id ? "Editar Plantilla" : "Nueva Plantilla"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="label-xs text-muted-foreground">Nombre</Label>
            <Input data-testid="template-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ej: Mes operativo estándar" className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="label-xs text-muted-foreground">Saldo inicial sugerido (USD)</Label>
            <Input data-testid="template-initial-balance-input" type="number" step="0.01"
              value={form.initial_balance}
              onChange={(e) => setForm({ ...form, initial_balance: e.target.value })}
              className="mt-1 rounded-sm font-mono-tabular" />
          </div>
          <div className="md:col-span-3">
            <Label className="label-xs text-muted-foreground">Descripción</Label>
            <Textarea data-testid="template-description-input" rows={2} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" />
          </div>
        </div>

        <div className="mt-2 border border-border rounded-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/40">
            <div>
              <div className="font-heading font-bold text-sm">Partidas de la plantilla</div>
              <div className="text-xs text-muted-foreground">El día del mes se convierte en fecha al crear el plan</div>
            </div>
            <Button data-testid="template-add-entry-btn" size="sm" variant="outline" className="rounded-sm"
              onClick={() => setForm((f) => ({ ...f, entries: [...f.entries, blankEntry()] }))}>
              <Plus size={14} className="mr-1" weight="bold" /> Agregar partida
            </Button>
          </div>
          {form.entries.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Sin partidas todavía.</div>
          ) : (
            <div className="divide-y divide-border">
              {form.entries.map((e, idx) => (
                <TemplateEntryRow key={e.key} idx={idx} entry={e} categories={categories}
                  onChange={(patch) => updateEntry(e.key, patch)}
                  onRemove={() => setForm((f) => ({ ...f, entries: f.entries.filter((x) => x.key !== e.key) }))} />
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button data-testid="template-save-btn" onClick={handleSave} disabled={saving} className="rounded-sm font-semibold">
            {saving ? "Guardando…" : "Guardar Plantilla"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TemplateEntryRow = ({ idx, entry, categories, onChange, onRemove }) => {
  const subs = categories.find((c) => c.name === entry.category && c.type === entry.type)?.subcategories || [];
  return (
    <div className="grid grid-cols-2 md:grid-cols-12 gap-2 p-3 items-end" data-testid={`template-entry-row-${idx}`}>
      <div className="md:col-span-2">
        <Label className="label-xs text-muted-foreground">Tipo</Label>
        <Select value={entry.type} onValueChange={(v) => onChange({ type: v, category: "", subcategory: "" })}>
          <SelectTrigger data-testid={`template-entry-type-${idx}`} className="mt-1 rounded-sm h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="income">Ingreso</SelectItem>
            <SelectItem value="expense">Egreso</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label className="label-xs text-muted-foreground">Categoría</Label>
        <Select value={entry.category} onValueChange={(v) => onChange({ category: v, subcategory: "" })}>
          <SelectTrigger data-testid={`template-entry-category-${idx}`} className="mt-1 rounded-sm h-9"><SelectValue placeholder="Elegir" /></SelectTrigger>
          <SelectContent>
            {categories.filter((c) => c.type === entry.type).map((c) => (
              <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label className="label-xs text-muted-foreground">Subcategoría</Label>
        <Select value={entry.subcategory || ""} onValueChange={(v) => onChange({ subcategory: v })}>
          <SelectTrigger data-testid={`template-entry-subcategory-${idx}`} className="mt-1 rounded-sm h-9"><SelectValue placeholder="Opcional" /></SelectTrigger>
          <SelectContent>
            {subs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-3">
        <Label className="label-xs text-muted-foreground">Descripción</Label>
        <Input data-testid={`template-entry-description-${idx}`} value={entry.description}
          onChange={(e) => onChange({ description: e.target.value })} className="mt-1 rounded-sm h-9" />
      </div>
      <div className="md:col-span-1">
        <Label className="label-xs text-muted-foreground">Monto</Label>
        <Input data-testid={`template-entry-amount-${idx}`} type="number" step="0.01" value={entry.amount}
          onChange={(e) => onChange({ amount: e.target.value })} className="mt-1 rounded-sm h-9 font-mono-tabular" />
      </div>
      <div className="md:col-span-1">
        <Label className="label-xs text-muted-foreground">Día</Label>
        <Input data-testid={`template-entry-day-${idx}`} type="number" min={1} max={31} value={entry.day_of_month}
          onChange={(e) => onChange({ day_of_month: e.target.value })} className="mt-1 rounded-sm h-9 font-mono-tabular" />
      </div>
      <div className="md:col-span-1 flex justify-end">
        <button data-testid={`template-entry-remove-${idx}`} onClick={onRemove}
          className="p-2 rounded-sm text-muted-foreground hover:text-destructive hover:bg-muted transition-colors">
          <Trash size={16} />
        </button>
      </div>
    </div>
  );
};
